import { formatMoney, formatPercent } from "@/lib/utils";

/** What one discount chain takes off list, as a percent: "30/30/5" → 53.45. */
export function chainPercentOff(chain: string | null | undefined): number | null {
  const parts = (chain ?? "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .map(Number);
  if (!parts.length || parts.some((part) => !Number.isFinite(part) || part < 0 || part > 100))
    return null;
  const remaining = parts.reduce((left, part) => left * (1 - part / 100), 1);
  return (1 - remaining) * 100;
}

export interface BestDiscount {
  chain: string;
  off: number;
  percentOff: string;
  /** The category it applies to, or null when every category gets it. */
  category: string | null;
}

/** The deepest discount among a source's categories, and where it applies. */
export function bestDiscount(
  rows: Array<{ chain: string | null | undefined; label: string | null | undefined }>,
): BestDiscount | null {
  const priced = rows.flatMap((row) => {
    const off = chainPercentOff(row.chain);
    return off != null && off > 0 ? [{ ...row, chain: row.chain!.replace(/\s/g, ""), off }] : [];
  });
  const best = priced.reduce<(typeof priced)[number] | null>(
    (top, row) => (top == null || row.off > top.off + 1e-9 ? row : top),
    null,
  );
  if (!best) return null;
  const everywhere = rows.every((row) => Math.abs((chainPercentOff(row.chain) ?? -1) - best.off) < 1e-9);
  return {
    chain: best.chain,
    off: best.off,
    percentOff: `${formatPercent(best.off)} off`,
    category: everywhere ? null : best.label?.trim() || null,
  };
}

export const FREIGHT_BOOKING_LABELS: Record<string, string> = {
  manufacturer: "Manufacturer books freight",
  usa_rs: "We book freight",
};

/** A source's small-order policy in a few words: "$100 fee under $1,000.00", "None". */
export function describeOrderFees(
  policyType: string | null | undefined,
  threshold: string | number | null | undefined,
  fee: string | number | null | undefined,
): string {
  switch (policyType) {
    case "fixed_fee":
      return `${formatMoney(fee)} fee under ${formatMoney(threshold)}`;
    case "top_up":
      return `Top up to ${formatMoney(threshold)}`;
    case "hard_minimum":
      return `${formatMoney(threshold)} minimum`;
    default:
      return "None";
  }
}

type FeeRule = {
  label: string;
  amount: string | number;
  order_under?: string | number | null;
  min_length_in?: string | number | null;
  max_length_in?: string | number | null;
};

function lengthRange(rule: FeeRule): string {
  const min = rule.min_length_in == null ? null : Number(rule.min_length_in);
  const max = rule.max_length_in == null ? null : Number(rule.max_length_in);
  if (min !== null && max !== null) return `${min}–${max} in`;
  if (min !== null) return `${min} in and longer`;
  return `up to ${max} in`;
}

/** One of a manufacturer's other fees (USA-1049): "Handling Fee $20.00 under $200.00". */
export function describeFeeRule(rule: FeeRule): string {
  return rule.order_under != null
    ? `${rule.label} ${formatMoney(rule.amount)} under ${formatMoney(rule.order_under)}`
    : `${rule.label} ${formatMoney(rule.amount)} per item, ${lengthRange(rule)}`;
}

/** The manufacturer's other fees, counted: "+3 other fees" (USA-1049). */
export function describeOtherFees(count: number): string | null {
  if (count === 0) return null;
  return `+${count} other ${count === 1 ? "fee" : "fees"}`;
}
